import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { SERVER } from '../../../config/config';
import { 
  FaUsers, FaEye, FaChartLine, FaUserPlus, 
  FaUserCheck, FaWhatsapp, FaEnvelope, FaMobileAlt, FaGlobeAmericas, FaBolt,
  FaFacebook, FaLinkedin, FaInstagram
} from 'react-icons/fa';
import { BsClockFill } from "react-icons/bs";
import { GiChart } from "react-icons/gi";

import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const Analytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30');

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - parseInt(dateRange));
      
      const response = await axios.get(`${SERVER}/analytics/dashboard`, {
        params: {
          startDate: startDate.toISOString(),
          endDate: new Date().toISOString()
        },
        withCredentials: true
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">No analytics data available</p>
      </div>
    );
  }

  const { summary, demographics, mostVisitedPages, trafficSources, conversionEvents, exitPages, dailyTrend } = analytics;

  const conversionMap = conversionEvents.reduce((acc, event) => {
    acc[event.event_type] = parseInt(event.count);
    return acc;
  }, {});

  // Prepare Device Data for Pie Chart
  const deviceData = [
    { name: 'Mobile', value: demographics.devices?.mobile },
    { name: 'Desktop', value: demographics.devices?.desktop },
    { name: 'Tablet', value: demographics.devices?.tablet },
  ].filter(d => d.value > 0);

  // Helper function to get friendly page names
  const getPageName = (path, title) => {
    const pageNames = {
      // Main Website Pages
      '/': 'Home Page',
      '/about': 'About Us',
      '/services': 'Services',
      '/portfolio': 'Portfolio',
      '/studio': 'Creative Studio',
      '/contact': 'Contact',
      '/book-consultation': 'Book Consultation',
      
      // Dynamic Routes
      '/services/web-development': 'Web Development Service',
      '/services/mobile-app': 'Mobile App Service',
      '/services/ui-ux-design': 'UI/UX Design Service',
      '/services/digital-marketing': 'Digital Marketing Service',
      '/services/ecommerce': 'E-commerce Service',
      '/services/branding': 'Branding Service',
      
      // Admin Pages
      '/admin/login': 'Admin Login',
      '/admin/dashboard': 'Admin Dashboard',
      '/admin/forgot-password': 'Forgot Password',
      '/admin/reset-password': 'Reset Password',
    };

    // Handle dynamic portfolio routes
    if (path.startsWith('/portfolio/') && path !== '/portfolio') {
      return `Project: ${path.split('/')[2].replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}`;
    }

    // Handle dynamic service routes
    if (path.startsWith('/services/') && path !== '/services') {
      const serviceName = path.split('/')[2].replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      return `Service: ${serviceName}`;
    }

    // Use predefined page names first
    if (pageNames[path]) return pageNames[path];
    
    // Use title if it's meaningful and not too long
    if (title && 
        title !== 'undefined' && 
        !title.toLowerCase().includes('wetekup') && 
        !title.toLowerCase().includes('we tekup') &&
        title.length < 60 && 
        title.length > 3) {
      return title;
    }
    
    // Fallback to formatted path
    if (path === '/') return 'Home Page';
    return path.split('/').filter(Boolean).map(segment => 
      segment.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    ).join(' > ');
  };

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
  
  // Re-usable glass card class
  const glassCardClass = "bg-white/80 backdrop-blur-sm border border-white/40 shadow-xl rounded-2xl";
  const glassInputClass = "bg-white/50 border border-white/60 focus:ring-2 focus:ring-blue-500/50 rounded-xl backdrop-blur-md";

  return (
    <div className="space-y-8 animate-fadeIn p-6 rounded-3xl bg-gradient-to-br from-indigo-50/50 via-purple-50/50 to-teal-50/50 min-h-screen">
      {/* Date Range Selector & Header */}
      <div className="flex justify-between items-center" style={{ animationDelay: '0.1s' }}>
        <div>
            <h2 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600">
                Analytics Control
            </h2>
            <p className="text-gray-500 text-sm mt-1">Monitor your performance in real-time</p>
        </div>
        <select
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value)}
          className={`px-4 py-2 ${glassInputClass} text-gray-700 outline-none transition-all hover:bg-white/70`}
        >
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
        </select>
      </div>

      {/* Real-time & Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 animate-slideUp" style={{ animationDelay: '0.2s' }}>
        <div className="bg-gradient-to-br from-blue-600/90 to-indigo-600/90 backdrop-blur-xl rounded-2xl p-6 shadow-2xl text-white transform transition-all hover:scale-105 border border-white/20">
            <div className="flex justify-between items-center mb-4">
                <span className="text-blue-100 font-medium tracking-wide">Active Now</span>
                <div className="relative">
                    <FaBolt className="text-yellow-300 relative z-10" />
                    <span className="absolute top-0 left-0 -ml-1 -mt-1 w-6 h-6 bg-yellow-400 rounded-full animate-ping opacity-75"></span>
                </div>
            </div>
            <div className="text-5xl font-bold tracking-tight">{summary.activeUsers}</div>
            <div className="text-blue-200 text-xs mt-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                Live updates active
            </div>
        </div>

        <StatCard
          icon={<FaUsers className="text-blue-600" size={32} />}
          title="Total Visitors"
          value={summary.totalVisitors}
          delay="0"
        />
        <StatCard
          icon={<FaEye className="text-emerald-600" size={32} />}
          title="Page Views"
          value={summary.totalPageViews}
          delay="100"
        />
        <StatCard
          icon={<GiChart  className="text-purple-600" size={32} />}
          title="Bounce Rate"
          value={`${summary.bounceRate}%`}
          delay="200"
        />
      </div>

      {/* Secondary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 animate-slideUp" style={{ animationDelay: '0.3s' }}>
        <StatCard
            icon={<BsClockFill className="text-orange-600" size={32} />}
            title="Avg Time (sec)"
            value={summary.avgTimeSpent}
            delay="300"
        />
        <StatCard
          icon={<FaUserPlus className="text-teal-600" size={32} />}
          title="New Visitors"
          value={summary.newVisitors}
          delay="400"
        />
        <StatCard
          icon={<FaUserCheck className="text-indigo-600" size={32} />}
          title="Returning"
          value={summary.returningVisitors}
          delay="500"
        />
      </div>

      {/* Page Analytics Section */}
      <div className="animate-slideUp" style={{ animationDelay: '0.35s' }}>
        <div className={`${glassCardClass} p-6`}>
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-indigo-100/50 rounded-xl text-indigo-600">
              <FaChartLine size={20} />
            </div>
            <h3 className="font-bold text-gray-800 text-lg">Website Pages Performance</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
            {/* Page-specific metrics */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200/50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-blue-700 font-medium text-sm">Home Page</span>
                <FaEye className="text-blue-600" size={16} />
              </div>
              <div className="text-2xl font-bold text-blue-800">
                {mostVisitedPages.find(p => p.page_path === '/')?.views || 0}
              </div>
              <div className="text-blue-600 text-xs mt-1">views</div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border border-green-200/50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-green-700 font-medium text-sm">About Us</span>
                <FaUsers className="text-green-600" size={16} />
              </div>
              <div className="text-2xl font-bold text-green-800">
                {mostVisitedPages.find(p => p.page_path === '/about')?.views || 0}
              </div>
              <div className="text-green-600 text-xs mt-1">views</div>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200/50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-purple-700 font-medium text-sm">Services</span>
                <GiChart className="text-purple-600" size={16} />
              </div>
              <div className="text-2xl font-bold text-purple-800">
                {mostVisitedPages.filter(p => p.page_path.startsWith('/services')).reduce((sum, p) => sum + parseInt(p.views), 0)}
              </div>
              <div className="text-purple-600 text-xs mt-1">total views</div>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 border border-orange-200/50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-orange-700 font-medium text-sm">Portfolio</span>
                <FaEye className="text-orange-600" size={16} />
              </div>
              <div className="text-2xl font-bold text-orange-800">
                {mostVisitedPages.filter(p => p.page_path.startsWith('/portfolio')).reduce((sum, p) => sum + parseInt(p.views), 0)}
              </div>
              <div className="text-orange-600 text-xs mt-1">total views</div>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl p-4 border border-teal-200/50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-teal-700 font-medium text-sm">Studio</span>
                <FaChartLine className="text-teal-600" size={16} />
              </div>
              <div className="text-2xl font-bold text-teal-800">
                {mostVisitedPages.find(p => p.page_path === '/studio')?.views || 0}
              </div>
              <div className="text-teal-600 text-xs mt-1">views</div>
            </div>

            <div className="bg-gradient-to-br from-pink-50 to-pink-100 rounded-xl p-4 border border-pink-200/50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-pink-700 font-medium text-sm">Contact</span>
                <FaEnvelope className="text-pink-600" size={16} />
              </div>
              <div className="text-2xl font-bold text-pink-800">
                {mostVisitedPages.find(p => p.page_path === '/contact')?.views || 0}
              </div>
              <div className="text-pink-600 text-xs mt-1">views</div>
            </div>
          </div>

          {/* Top performing pages list */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-gray-700 text-sm uppercase tracking-wide">Top Performing Pages</h4>
              <div className="text-xs text-gray-500">
                Showing top {mostVisitedPages.filter(p => !p.page_path.startsWith('/admin')).slice(0, 8).length} pages
              </div>
            </div>
            {mostVisitedPages
              .filter(p => !p.page_path.startsWith('/admin'))
              .slice(0, 8)
              .map((page, idx) => {
                const isHighPerforming = page.views > (summary.totalPageViews / mostVisitedPages.length);
                const avgTime = Math.round(page.avg_time_spent);
                const isGoodEngagement = avgTime > 30; // More than 30 seconds is good engagement
                
                return (
                  <div key={idx} className="flex items-center justify-between p-3 bg-white/40 rounded-lg hover:bg-white/60 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-xs ${
                        isHighPerforming 
                          ? 'bg-gradient-to-br from-green-500 to-emerald-600' 
                          : 'bg-gradient-to-br from-blue-500 to-purple-600'
                      }`}>
                        {idx + 1}
                      </div>
                      <div>
                        <div className="font-medium text-gray-800 text-sm">
                          {getPageName(page.page_path, page.page_title)}
                        </div>
                        <div className="text-xs text-gray-500 font-mono">
                          {page.page_path}
                        </div>
                      </div>
                      {isHighPerforming && (
                        <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                          High Traffic
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <div className="text-center">
                        <div className="font-bold text-gray-800">{page.views}</div>
                        <div className="text-xs text-gray-500">views</div>
                      </div>
                      <div className="text-center">
                        <div className={`font-bold ${isGoodEngagement ? 'text-green-600' : 'text-gray-800'}`}>
                          {avgTime}s
                        </div>
                        <div className="text-xs text-gray-500">avg time</div>
                      </div>
                      <div className="text-center">
                        <div className="font-bold text-gray-800">
                          {Math.round((page.views / summary.totalPageViews) * 100)}%
                        </div>
                        <div className="text-xs text-gray-500">share</div>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>

          {/* Conversion Funnel for Book Consultation */}
          <div className="mt-8 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200/50">
            <h4 className="font-semibold text-gray-700 text-sm uppercase tracking-wide mb-4">Conversion Funnel</h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {mostVisitedPages.find(p => p.page_path === '/')?.views || 0}
                </div>
                <div className="text-xs text-gray-600 mt-1">Home Visitors</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">
                  {mostVisitedPages.filter(p => p.page_path.startsWith('/services')).reduce((sum, p) => sum + parseInt(p.views), 0)}
                </div>
                <div className="text-xs text-gray-600 mt-1">Services Interest</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">
                  {mostVisitedPages.find(p => p.page_path === '/contact')?.views || 0}
                </div>
                <div className="text-xs text-gray-600 mt-1">Contact Page</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {mostVisitedPages.find(p => p.page_path === '/book-consultation')?.views || 0}
                </div>
                <div className="text-xs text-gray-600 mt-1">Consultations</div>
              </div>
            </div>
            <div className="mt-4 text-center">
              <div className="text-sm text-gray-600">
                Conversion Rate: <span className="font-bold text-green-600">
                  {summary.totalVisitors > 0 
                    ? Math.round(((mostVisitedPages.find(p => p.page_path === '/book-consultation')?.views || 0) / summary.totalVisitors) * 100)
                    : 0}%
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Demographics & Tech (New Section) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-slideUp" style={{ animationDelay: '0.4s' }}>
          {/* Countries */}
          <div className={`${glassCardClass} p-6`}>
              <div className="flex items-center gap-3 mb-6">
                  <div className="p-3 bg-blue-100/50 rounded-xl text-blue-600">
                    <FaGlobeAmericas size={20} />
                  </div>
                  <h3 className="font-bold text-gray-800 text-lg">Top Countries</h3>
              </div>
              <div className="space-y-4">
                  {demographics.countries.length > 0 ? demographics.countries.map((country, idx) => (
                      <div key={idx} className="flex justify-between items-center text-sm group cursor-default">
                          <span className="text-gray-600 font-medium">{country.name}</span>
                          <div className="flex items-center gap-3 w-1/2 justify-end">
                              <div className="w-24 bg-gray-200/50 rounded-full h-2 overflow-hidden">
                                  <div className="bg-blue-500 h-full rounded-full transition-all duration-1000" style={{ width: `${(country.count / summary.totalVisitors) * 100}%` }}></div>
                              </div>
                              <span className="font-bold text-gray-800 w-8 text-right">{country.count}</span>
                          </div>
                      </div>
                  )) : <p className="text-sm text-gray-500">No geographic data yet</p>}
              </div>
          </div>

          {/* Devices */}
          <div className={`${glassCardClass} p-6`}>
              <div className="flex items-center gap-3 mb-6">
                  <div className="p-3 bg-green-100/50 rounded-xl text-green-600">
                    <FaMobileAlt size={20} />
                  </div>
                  <h3 className="font-bold text-gray-800 text-lg">Devices</h3>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                      <Pie
                          data={deviceData}
                          innerRadius={60}
                          outerRadius={90}
                          paddingAngle={8}
                          dataKey="value"
                          stroke="none"
                      >
                          {deviceData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.8)', backdropFilter: 'blur(10px)', borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' }}
                      />
                      <Legend verticalAlign="bottom" height={36}/>
                  </PieChart>
              </ResponsiveContainer>
          </div>

          {/* Browsers */}
          <div className={`${glassCardClass} p-6`}>
            <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-purple-100/50 rounded-xl text-purple-600">
                    <FaChartLine size={20} />
                </div>
                <h3 className="font-bold text-gray-800 text-lg">Top Browsers</h3>
            </div>
            <div className="space-y-3">
                {demographics.browsers.map((browser, idx) => (
                    <div key={idx} className="flex justify-between items-center p-3 hover:bg-white/40 rounded-xl transition-colors cursor-default">
                        <span className="text-gray-600 font-medium">{browser.name}</span>
                        <span className="font-bold text-gray-800 bg-white/60 px-3 py-1 rounded-lg border border-white/40">{browser.count}</span>
                    </div>
                ))}
            </div>
          </div>
      </div>

      {/* Main Charts Area */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-slideUp" style={{ animationDelay: '0.5s' }}>
          {/* Traffic Sources */}
          <div className={`${glassCardClass} p-6`}>
            <h3 className="text-xl font-bold text-gray-800 mb-6">Traffic Sources</h3>
            <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                    <Pie
                        data={trafficSources.map(s => ({ name: s.source, value: parseInt(s.count) }))}
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        innerRadius={60}
                        fill="#8884d8"
                        dataKey="value"
                        paddingAngle={5}
                        stroke="none"
                    >
                        {trafficSources.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.8)', backdropFilter: 'blur(4px)', borderRadius: '8px', border: 'none' }} />
                    <Legend />
                </PieChart>
            </ResponsiveContainer>
          </div>
                        
         {/* Daily Trend Chart */}
         <div className={`${glassCardClass} p-6`}>
          <h3 className="text-xl font-bold text-gray-800 mb-6">Daily Visitors Trend</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={dailyTrend.map(d => ({ 
                ...d, 
                date: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
            }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#6b7280' }} axisLine={false} tickLine={false} dy={10} />
              <YAxis tick={{ fontSize: 12, fill: '#6b7280' }} axisLine={false} tickLine={false} />
              <Tooltip 
                cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }}
                contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.8)', backdropFilter: 'blur(4px)', borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }} 
              />
              <Bar dataKey="visitors" fill="#3b82f6" radius={[6, 6, 0, 0]} name="Visitors" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Tables Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-slideUp" style={{ animationDelay: '0.6s' }}>
        {/* Most Visited Pages */}
        <div className={`${glassCardClass} overflow-hidden`}>
            <div className="p-6 border-b border-white/30 bg-white/20">
                <h3 className="text-xl font-bold text-gray-800">Most Visited Pages</h3>
            </div>
            <div className="overflow-x-auto">
                <table className="min-w-full">
                    <thead className="bg-white/30 backdrop-blur-sm">
                        <tr>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">IP Address</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Page</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Views</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Time</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-white/20">
                        {mostVisitedPages.filter(p => !p.page_path.startsWith('/admin')).slice(0, 5).map((page, idx) => (
                            <tr key={idx} className="hover:bg-white/40 transition-colors">
                                <td className="px-6 py-4 text-sm text-gray-600 font-mono bg-white/30 rounded-r-lg inline-block my-2 mx-4 border border-white/50 shadow-sm">{page.ip_address}</td>
                                <td className="px-6 py-4 text-sm text-gray-800 font-medium truncate max-w-xs">{getPageName(page.page_path, page.page_title)}</td>
                                <td className="px-6 py-4 text-sm text-gray-600">{page.views}</td>
                                <td className="px-6 py-4 text-sm text-gray-600">{Math.round(page.avg_time_spent)}s</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>

        {/* Conversion & Exit */}
        <div className="space-y-6">
            <div className={`${glassCardClass} p-6`}>
                <h3 className="text-xl font-bold text-gray-800 mb-6">Conversions</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <ConversionCard title="Forms" count={conversionMap.form_submit || 0} icon={<FaEnvelope className="text-blue-500" size={32} />} />
                    <ConversionCard title="WhatsApp" count={conversionMap.whatsapp || 0} icon={<FaWhatsapp className="text-green-500" size={32} />} />
                    <ConversionCard title="Facebook" count={conversionMap.facebook_click || 0} icon={<FaFacebook className="text-blue-700" size={32} />} />
                    <ConversionCard title="LinkedIn" count={conversionMap.linkedin_click || 0} icon={<FaLinkedin className="text-blue-600" size={32} />} />
                    <ConversionCard title="Instagram" count={conversionMap.instagram_click || 0} icon={<FaInstagram className="text-pink-600" size={32} />} />
                </div>
            </div>
            
            <div className={`${glassCardClass} p-6`}>
                <h3 className="text-xl font-bold text-gray-800 mb-6">Top Exit Pages</h3>
                <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                        layout="vertical"
                        data={exitPages.filter(p => !p.page_path.startsWith('/admin')).slice(0, 5)}
                        margin={{ top: 5, right: 30, left: 40, bottom: 5 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e5e7eb" />
                        <XAxis type="number" hide />
                        <YAxis 
                            dataKey="page_title" 
                            type="category" 
                            width={100}
                            tick={{ fontSize: 11, fill: '#4b5563' }}
                            tickFormatter={(value) => value && value.length > 15 ? `${value.substring(0, 15)}...` : value}
                        />
                        <Tooltip 
                            cursor={{ fill: 'rgba(239, 68, 68, 0.1)' }}
                            contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.9)', backdropFilter: 'blur(4px)', borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                        />
                        <Bar dataKey="exit_count" fill="#ef4444" radius={[0, 4, 4, 0]} barSize={20} name="Exits">
                             {exitPages.map((entry, index) => (
                                <Cell key={`cell-${index}`} fillOpacity={0.8} />
                             ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon, title, value, delay }) => {
  return (
    <div 
        className="bg-white/80 backdrop-blur-sm border border-white/40 shadow-lg rounded-2xl p-6 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:bg-white/90 group"
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-gray-500 mb-1 tracking-wide uppercase">{title}</p>
          <p className="text-3xl font-extrabold text-gray-800 group-hover:text-blue-600 transition-colors">{value}</p>
        </div>
        <div className="text-4xl opacity-90 transition-all">
            {icon}
        </div>
      </div>
    </div>
  );
};

// Conversion Card Component
const ConversionCard = ({ icon, title, count }) => (
  <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 flex flex-col items-center justify-center gap-2 border border-white/30 hover:bg-white/90 transition-all hover:scale-105 hover:shadow-lg cursor-default group">
    <div className="text-3xl group-hover:scale-110 transition-transform">{icon}</div>
    <div className="text-center">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{title}</p>
      <p className="text-2xl font-bold text-gray-800">{count}</p>
    </div>
  </div>
);

export default Analytics;
